import { Box, Spacer, Text } from '@jfs/ui-new'
import { getErrorMessage, getRandomAlphaNumString } from '@jfs/utils'
import { useIsFocused } from '@react-navigation/native'
import * as React from 'react'
import { FC, ReactElement, useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { Dimensions } from 'react-native'
import { GestureHandlerGestureEvent } from 'react-native-gesture-handler'
import { CalendarTypes } from '../../../comps/Calendar/types'
import CalendarViewModal from '../../../comps/CalendarViewModal'
import EmptyView from '../../../comps/EmptyView'
import { ModBox } from '../../../comps/ModifiedBox/ModifiedBox'
import Tabbar, { TabbarType, TabData } from '../../../comps/Tabbar'
import {
  TitleSubtitleLayout,
  TitleSubtitleLayoutColorTheme,
  TitleSubtitleLayoutTypes
} from '../../../comps/TitleSubtitleLayoutNew'
import { highlightLabelArray } from '../../../data/static/data'
import { actionMenuStrings, staticStrings, transactionScreenStrings } from '../../../data/static/strings'
import { useLazyApiData, useStores } from '../../../data/store'
import { calendarWebWidth, getRangeDate, getYesterday, useWindowDimensions } from '../../../util/util'
import { styles } from './style'
export interface HighlightsProps {
  title?: string
  toggleLoader: (data: boolean) => void
  canViewHighlightsData: boolean
}

const stats = [
  { title: 'Today' },
  { title: 'Yesterday' },
  { title: 'Last 7 days' },
  { title: 'Last 30 days' },
  { title: 'Custom Dates', iconId: 'CALENDAR' },
]

const renderItem = ({ item }: { item: any }): JSX.Element => {
  return (
    <TitleSubtitleLayout
      type={TitleSubtitleLayoutTypes.BOLD_TITLE_TRENDS}
      placeHolder={item.title}
      color={TitleSubtitleLayoutColorTheme.BOLD_TITLE_TRENDS}
      title={item.subtitle}
      tooltip={item?.tooltip ?? ''}
      hideIcon={false}
      key={getRandomAlphaNumString(3)}
    />
  )
}

const Highlights: FC<HighlightsProps> = ({ canViewHighlightsData = true }) => {
  const { date, setNewDate, getStats, mid, fetchStats, setMessage, dateLabel, dateTabIndex, setDateTabIndex } =
    useStores((store) => ({
      getStats: store?.settings?.metaData?.getStats,
      fetchStats: store?.settings?.metaData?.fetchStatsV3,
      dateTabIndex: store?.settings?.metaData.dateTabIndex,
      setDateTabIndex: store?.settings?.metaData.setDateTabIndex,
      mid: store?.user?.selectedMid,
      totalRefundAmount: store?.settings?.metaData?.totalRefundAmount,
      date: store?.settings?.metaData?.selectedDate,
      setNewDate: store?.settings?.metaData?.updateDates,
      dateLabel: store?.settings?.metaData?.selectedDate?.dateLabel,
      setMessage: store?.response?.setMessage,
    }))
  const [fetchStatsApi, apiHandler] = useLazyApiData(fetchStats)
  // const [fetchRefundStatsApi] = useLazyApiData(fetchStats)
  const isFocused = useIsFocused()
  const getSelectedDate = useCallback(
    (type: number): Date => {
      const getSelectedValue: any = {
        0: getTodayValue(),
        1: getYesterdayValue(),
        2: getDateValue(7),
        3: getDateValue(30),
      }
      return getSelectedValue[type]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  useEffect(() => {
    if (mid && isFocused) {
      fetchStatsApi(mid, 'transactions', undefined, false, dateLabel === 'custom')
      if (dateLabel === 'custom') {
        updateCustomDateString(date.fromDate, date.toDate)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStatsApi, mid, isFocused, dateTabIndex, setNewDate, dateLabel, date.fromDate, date.toDate])

  const handleRetry = () => {
    if (mid && isFocused) {
      fetchStatsApi(mid, 'transactions', undefined, false, dateLabel === 'custom')
    }
  }

  const viewPortWidth = useWindowDimensions()

  const themedStyle = styles(viewPortWidth)

  const [visible, setVisible] = useState(false)
  const [tabbarData, setTabbarData] = useState<TabData[]>(stats)
  const [layoutPos, setLayoutPos] = useState<any>('')
  const [calendarLayout, setCalendarLayout] = useState<any>('')
  const windowWidth = Dimensions.get('window').width
  const calendarLayoutWdith = calendarLayout?.layout?.width
  const remainingWidth = windowWidth - calendarLayoutWdith
  const isWebCalendarOpenInNative = windowWidth - calendarWebWidth < 200
  const [calenderPos, setCalenderPos] = useState<any>({ left: undefined, top: undefined, right: undefined })

  const onDateSelected = (firstdate: Date, secondDate?: Date): void => {
    if (secondDate) {
      updateCustomDateString(firstdate, secondDate)
      setVisible(false)
      setNewDate({
        fromDate: firstdate,
        toDate: secondDate,
        dateLabel: 'custom',
      })
      setDateTabIndex(dateTabIndex < 4 ? 4 : dateTabIndex + 1)
    }
  }

  const updateCustomDateString = (fromDate: any, toDate: any) => {
    if (dateLabel === 'custom') {
      const dateString = getRangeDate(fromDate, toDate)
      const updatedStatistics = stats.slice()
      const items: TabData[] = []
      updatedStatistics.map((item) => {
        if (item.title === 'Custom Dates') {
          items.push({ title: dateString, iconId: item.iconId })
        } else {
          items.push(item)
        }
        return item
      })
      setTabbarData(items)
    }
  }

  const getListData = () => {
    const statsData = getStats()
    const retData = statsData.map((item: string, index: number) => ({
      title: item,
      subtitle: highlightLabelArray[index].caption,
      tooltip: highlightLabelArray[index].tooltip,
    }))

    return retData
  }
  const ListData = getListData()
  const getDateValue = (dateValue: number): Date => {
    const today = new Date(Date.now())
    return new Date(today.setDate(today.getDate() - dateValue))
  }

  const getYesterdayValue = useCallback(() => {
    const yesterdayBegin = getYesterday('START')
    const yesterdayEnd = getYesterday('END')
    setNewDate({
      fromDate: yesterdayBegin,
      toDate: yesterdayEnd,
      dateLabel: actionMenuStrings.yesterday,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getTodayValue = (): Date => {
    const today = new Date(Date.now())
    return new Date(today.setHours(0, 0, 0, 0))
  }

  const handleSelected = (index: number): void => {
    if (index === 1) {
      setDateTabIndex(index)
      getSelectedDate(index)
    } else if (index < 4) {
      setDateTabIndex(index)
      const today = new Date(Date.now())
      const currentDay = today
      const finishDay = getSelectedDate(index)
      setNewDate({
        fromDate: finishDay,
        toDate: currentDay,
        dateLabel: 'specific',
      })
    } else {
      setTimeout(() => {
        setVisible(true)
      }, 300)
    }
  }
  const close = (): void => {
    setVisible(false)
  }

  const selectedHandler = (index: number): void => {
    handleSelected(index)
  }

  const getListView = (): ReactElement[] => {
    return ListData.map((item: any) => {
      return renderItem({ item })
    })
  }

  const onCalendarLayout = (nativeEventData: GestureHandlerGestureEvent) => {
    if (calendarLayout === '') {
      setCalendarLayout(nativeEventData?.nativeEvent)
      layoutPos.target?.measure((...args: any) => {
        const left = !isWebCalendarOpenInNative ? args[4] - 170 - args[0] : undefined

        const top = args[5] - args[1] + 15
        const right = remainingWidth < calendarLayoutWdith || isWebCalendarOpenInNative ? 17 : undefined
        setCalenderPos({
          left,
          top,
          right,
        })
      })
    }
  }

  /*   const returnLeftValue = () => {
    if (remainingWidth < calendarLayoutWdith) {
      return undefined
    } else if (!isWebCalendarOpenInNative) {
      return layoutPos?.x - layoutPos?.offsetX - 30
    } else {
      return undefined
    }
  }

  const returnTopValue = () => {
    return layoutPos?.y - layoutPos?.offsetY + 15
  }

  const returnRightValue = () => {
    if (remainingWidth < calendarLayoutWdith || isWebCalendarOpenInNative) {
      return 17
    } else {
      return undefined
    }
  } */

  const renderCalendarModal = (): ReactElement => {
    return (
      <ModBox testID="calendar-modal" {...styles(viewPortWidth).modal} show={visible}>
        <Box onPress={close} underlayColor={'TRANSPARENT'} {...styles(viewPortWidth).overlayStyle}>
          <Box
            onLayout={onCalendarLayout}
            {...styles(viewPortWidth).dropdownStyle(calenderPos.left, calenderPos.top, calenderPos.right)}
            backgroundColor="AVAILABLE_GREEN">
            <CalendarViewModal
              onDateSelection={onDateSelected}
              dateRange={getRangeDate(date.fromDate, date.toDate)}
              type={CalendarTypes.HISTORY}
              onCancel={close}
              maxDuration={31}
              sendError={(err: string) => setMessage(err)}
            />
          </Box>
        </Box>
      </ModBox>
    )
  }

  const renderPortal = (): React.ReactPortal => {
    return ReactDOM.createPortal(renderCalendarModal(), document.body)
  }

  const onLayout = (nativeEventData: any) => {
    setLayoutPos(nativeEventData?.nativeEvent)
    if (!calenderPos.top) {
      nativeEventData?.nativeEvent?.target?.measure((...args: any) => {
        const top = args[5] - args[1] + 15
        const left = args[4] - 170 - args[0]
        setCalenderPos({
          ...calenderPos,
          left,
          top,
        })
      })
    }
  }

  const errorState = getErrorMessage([apiHandler])
  return (
    <Box {...themedStyle.container}>
      {renderPortal()}
      <Text testID="homePageTitle" type={'h0Bold'}>
        {staticStrings.highlights}
      </Text>
      <Box {...themedStyle.row}>
        <Box {...themedStyle.tabbar}>
          <Tabbar
            default={dateTabIndex > 4 ? 4 : dateTabIndex}
            titles={tabbarData}
            onSelected={selectedHandler}
            type={TabbarType.highlights}
            onLayoutOnlyForPosition={4}
            onLayout={onLayout}
          />
        </Box>
      </Box>
      <Spacer hide={!canViewHighlightsData} size={24} direction={'vertical'} />
      <ModBox show={apiHandler?.state !== 'fulfilled'} h={80}>
        <EmptyView
          testID={'summery-empty-view'}
          onRetry={handleRetry}
          isLoading={apiHandler?.state === 'pending'}
          error={errorState}
          emptyMessage={transactionScreenStrings.empty}
          loadingMessage={transactionScreenStrings.loading}
          hide={apiHandler?.state === 'fulfilled'}
          loaderColor={'BLUE'}
        />
      </ModBox>
      <ModBox show={canViewHighlightsData && apiHandler?.state === 'fulfilled'} {...themedStyle.flexRow}>
        {getListView()}
      </ModBox>
    </Box>
  )
}

export default Highlights
